define('companiesListView',[
  'module',
  'underscore',
  'app',
  'jquery',
  'settings',
  'tableView',
  'companiesListDeleteView',
  'companiesListSortHeaderView',
  'companiesListFiltersView',
  'entities/companies/companies'
], function (
  Module,
  _,
  App,
  $,
  Settings,
  TableView,
  DeleteView,
  SortHeaderView,
  FiltersView,
  CompanyEntities
) {
  'use strict';

  var SORT_COOKIE = 'companiesListSort';
  var PAGE_COOKIE = 'companiesListPage';

  // Companies list. Reuses the shared paginated table (same component as the
  // order/offer lists); fetch/fetchCount hit the companies endpoints.
  Module.exports = TableView.extend({
    className: 'companies-list-view ' + TableView.prototype.className,
    pageSize: 50,
    pageMode: 'pagination',
    selectable: false,
    sortField: 'name',
    sortDirection: 'asc',
    filtersViewClass: FiltersView,

    // Restore the persisted sort + page size (cookies) before the table is built.
    initialize: function () {
      var savedSort = Settings.get(SORT_COOKIE, true);
      if (savedSort) {
        try {
          var sort = JSON.parse(savedSort);
          if (sort.sortField) {
            this.sortField = sort.sortField;
          }
          if (sort.sortDirection) {
            this.sortDirection = sort.sortDirection;
          }
        } catch (e) {
          // ignore a corrupt cookie
        }
      }

      var savedPage = this.readPageCookie();
      if (savedPage && savedPage.pageSize) {
        this.pageSize = savedPage.pageSize;
      }

      TableView.prototype.initialize.apply(this, arguments);
    },

    // Restore the persisted page once the table (body + footer) exists. The shared
    // table already dispatched its page-1 fetch in onShow; re-fetching the saved
    // offset aborts that in-flight request (see fetch), so page 1 is never rendered.
    onShow: function () {
      TableView.prototype.onShow.apply(this, arguments);

      var savedPage = this.readPageCookie();
      if (savedPage && savedPage.first > 0 && this.bodyView) {
        this.bodyView.first = savedPage.first;
        if (this.footerView) {
          this.footerView.currentPage = Math.floor(savedPage.first / this.pageSize) + 1;
        }
        this.bodyView.fetch();
      }
    },

    readPageCookie: function () {
      var saved = Settings.get(PAGE_COOKIE, true);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          // ignore a corrupt cookie
        }
      }
      return null;
    },

    setFields: function () {
      return [
        {
          name: 'name',
          label: _.i18n('common.name'),
          bind: 'name',
          className: 'name',
          sort: true,
          headerView: SortHeaderView
        }, {
          name: 'emailDomain',
          label: _.i18n('companies.emailDomain', null, 'Email domain'),
          bind: 'emailDomain',
          className: 'email-domain'
        }, {
          name: 'vatNumber',
          label: _.i18n('common.vatNumber', null, 'VAT'),
          bind: 'vatNumber',
          className: 'vat'
        }, {
          name: 'city',
          label: _.i18n('common.city'),
          bind: 'city',
          className: 'city'
        }, {
          name: 'countryCode',
          label: _.i18n('common.countryCode'),
          bind: 'countryCode',
          className: 'country'
        }, {
          name: 'nbrEndUsers',
          label: _.i18n('companies.nbrEndUsers', null, 'Sub-accounts'),
          bind: 'nbrEndUsers',
          className: 'nbr-endusers',
          sort: true,
          headerView: SortHeaderView
        }, {
          name: 'orderAmount',
          label: _.i18n('companies.orderAmount', null, 'Orders total'),
          bind: 'orderAmount',
          className: 'order-amount',
          sort: true,
          headerView: SortHeaderView,
          formatter: function (value) {
            var amount = (value === null || value === undefined) ? 0 : value;
            return Number(amount).toFixed(2) + ' €';
          }
        }, {
          name: 'delete',
          className: 'delete',
          view: DeleteView
        }
      ];
    },

    // Active filter values (set by the filters view), only non-empty ones.
    getFilterParams: function () {
      var params = {};
      if (this.filters) {
        var name = this.filters.get('name');
        var emailDomain = this.filters.get('emailDomain');
        var countryCode = this.filters.get('countryCode');
        if (name) { params.name = name; }
        if (emailDomain) { params.emailDomain = emailDomain; }
        if (countryCode) { params.countryCode = countryCode; }
      }
      return params;
    },

    fetch: function (first, pageSize) {
      var defer = $.Deferred();

      if (this.fetchRequest) {
        this.fetchRequest.abort();
      }

      // Remember the page we're on so returning to the list lands on it again.
      Settings.set(PAGE_COOKIE, JSON.stringify({ first: first, pageSize: pageSize }), true);

      var params = _.extend({
        first: first,
        pageSize: pageSize,
        sidx: this.sortField,
        sord: this.sortDirection
      }, this.getFilterParams());

      this.fetchRequest = $.ajax({
        type: 'GET',
        url: App.request('companies:list-url', params),
        success: _.bind(function (items) {
          var models = [];
          _.each(items, _.bind(function (item) {
            models.push(new CompanyEntities.Company(item));
          }, this));
          defer.resolve(models);
        }, this)
      });

      return defer.promise();
    },

    fetchCount: function () {
      var defer = $.Deferred();

      if (this.fetchCountRequest) {
        this.fetchCountRequest.abort();
      }

      this.fetchCountRequest = $.ajax({
        type: 'GET',
        url: App.request('companies:list-count-url', this.getFilterParams()),
        success: _.bind(function (counts) {
          defer.resolve(counts.totalCount);
        }, this)
      });

      return defer.promise();
    },

    onRowClick: function (view) {
      App.trigger('companies:show', view.model.get('secId'));
    },

    // Called by the sortable column headers: re-sort server-side from page 1.
    // Reset the body collection first so the rows re-render in the new order
    // (merging in place would keep shared rows at their old positions).
    setSort: function (field, direction) {
      this.sortField = field;
      this.sortDirection = direction;
      Settings.set(SORT_COOKIE, JSON.stringify({ sortField: field, sortDirection: direction }), true);
      if (this.footerView) {
        this.footerView.currentPage = 1;
      }
      this.bodyView.reset();
      this.refresh();
    }
  });
});

